import formData from './data'
import styles from './styles.module.scss'
import { z } from 'zod'
import { TextareaField } from '@components/ui/formElements/form/Textarea'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import SubmitButton, {
  SubmitButtonState
} from '@components/ui/Button/SubmitButton'
import { useState } from 'react'
import FormGrid from '@components/ui/formElements/form/shared/FormGrid'
import { InputField } from '@components/ui/formElements/form/InputField'
import Image from 'next/image'
import Link from '@components/ui/Link'
import SectionHeader from '@components/ui/SectionHeader'
import { getAnalyticsCountryCode, getLink } from '@helpers/env'
import { postFormRequest } from '@api/apiCalls'
import axios from 'axios'
import UnsubscribeFeedbackStatusMessages from './FeedbackStatusMessages'

export const UnsubscribeFormSchema = z.object({
  first_name: z.string().min(1, { message: 'First Name cannot be empty' }),
  last_name: z.string().min(1, { message: 'Last Name cannot be empty' }),
  address_line1: z.string().min(1, { message: 'Address 1 cannot be empty' }),
  postcode: z.string().min(1, { message: 'Postal code cannot be empty' }),
  unsubscribe_reason: TextareaField.getSchema(false, 200)
})

export type UnsubscribeFormData = z.infer<typeof UnsubscribeFormSchema>

const defaultFormValues: UnsubscribeFormData = {
  first_name: '',
  last_name: '',
  address_line1: '',
  postcode: '',
  unsubscribe_reason: ''
}

export default function UnsubscribePage() {
  const [buttonState, setButtonState] = useState<SubmitButtonState>('ready')
  const [serverError, setServerError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const methods = useForm<UnsubscribeFormData & { customError?: string }>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    resolver: zodResolver(UnsubscribeFormSchema),
    defaultValues: defaultFormValues
  })

  const { formState, handleSubmit } = methods
  const { errors, isDirty, isValid, isSubmitted } = formState

  const handleOnSubmit = async (values: UnsubscribeFormData) => {
    setServerError(null)

    const defaultPayload = {
      ...values,
      source: getAnalyticsCountryCode().toUpperCase()
    }

    try {
      setButtonState('loading')

      await postFormRequest(defaultPayload, formData.requestHandle)
      setSuccessMessage('Feedback form sent successfully')
      methods.reset(defaultFormValues)

      setButtonState('ready')

      window.location.href = getLink('/')
    } catch (e) {
      const error = e as Error
      setButtonState('ready')

      const serverMessage =
        (axios.isAxiosError(error) &&
          error.response &&
          error?.response?.data?.message) ||
        error?.message ||
        'Something went wrong while submitting the form. Please try again.'

      setServerError(serverMessage)
    }
  }

  const isSubmitDisabled = !isDirty || !isValid || buttonState === 'loading'

  const hasFieldErrors = Object.keys(errors).length > 0

  return (
    <div className={styles.UnsubscribePage}>
      <div className={styles.Image}>
        <Image
          src="/static/images/pages/unsubscribe/catalogue.png"
          fill
          alt="Unsubscribe from our catalogue"
        />
      </div>
      <div className={styles.Content}>
        <SectionHeader
          title="Unsubscribe from the catalogue"
          variant="left_aligned"
        />
        <FormProvider {...methods}>
          <p className={styles.Content__copy}>
            No longer wish to receive our catalogue? No problem — just fill in
            the form below:
          </p>

          <form
            onSubmit={handleSubmit(handleOnSubmit)}
            noValidate
            className={styles.Content__form}
          >
            <FormGrid gap="large">
              {formData.content.map(field => {
                if (field.type === 'textarea') {
                  return (
                    <TextareaField
                      key={field.id}
                      id={field.id}
                      required={field.required}
                      placeholder={field.placeholder}
                      name={field.question}
                    />
                  )
                }
                const shuldShowHelperMessage = field.id === 'address_line1'
                return (
                  <InputField
                    key={field.id}
                    name={field.id}
                    required={field.required}
                    label={field.question}
                    placeholder={field.placeholder}
                    helperMessage={
                      shuldShowHelperMessage
                        ? 'As it appears on the catalogue / postcard'
                        : undefined
                    }
                  />
                )
              })}
            </FormGrid>
            <UnsubscribeFeedbackStatusMessages
              serverError={serverError}
              successMessage={successMessage}
              hasFieldErrors={hasFieldErrors}
              isSubmitted={isSubmitted}
            />
            <div className={styles.Content__formButtons}>
              <SubmitButton
                state={buttonState}
                disabled={isSubmitDisabled}
                type="submit"
                fullWidth="inherit"
                className={styles.submit}
              >
                Submit
              </SubmitButton>
            </div>
            <div className={styles.Content__formHelperCopy}>
              <p>
                It’s worth noting that unsubscribing can take up to six weeks to
                take effect due to the printing planning cycles.
              </p>
              <p>
                Any questions?{' '}
                <Link
                  href="/pages/contact-us"
                  text="Get in touch with us"
                  textSize="large"
                />
                .
              </p>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  )
}
